import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {TextUtils} from "../../../../../../shared/utils/text-utils";
import {ProductBackorderStatus, ProductStockStatus, ProductVariantStatus} from "../../../ecommerce-product.enum";
import {IOption, IVariation} from "../add-product-form.interface";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {ProductAddHelperService} from "../product-add-helper.service";
import {DbActiveModal} from "../../../../../../modules/modal/modal-ref";
import {MediaLibraryComponent} from "../components/media-library/media-library.component";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {IMedia} from "../../../ecommerce-product.interface";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";

@Component({
    selector: 'db-product-variants-dialog',
    templateUrl: './product-variants-dialog.component.html',
    styleUrls: ['./product-variants-dialog.component.scss'],
    standalone: false
})
export class ProductVariantsDialogComponent implements OnInit, OnDestroy {
  heads = [TextUtils.getText('text_number'), TextUtils.getText('text_mainImage'), TextUtils.getText('text_option'), TextUtils.getText('text_stockStatus'), TextUtils.getText('text_sku'), TextUtils.getText('text_price'), TextUtils.getText('text_salePrice'), TextUtils.getText('text_weight'), TextUtils.getText('text_length'), TextUtils.getText('text_height'), TextUtils.getText('text_width'), ''];
  headsMain = [TextUtils.getText('text_number'), TextUtils.getText('text_mainImage'), TextUtils.getText('text_option'), TextUtils.getText('text_stockStatus'), TextUtils.getText('text_sku'), TextUtils.getText('text_price'), TextUtils.getText('text_salePrice'), TextUtils.getText('text_weight'), TextUtils.getText('text_length'), TextUtils.getText('text_height'), TextUtils.getText('text_width'), ''];
  heads2 = [TextUtils.getText('text_number'), TextUtils.getText('text_mainImage'), TextUtils.getText('text_option'), TextUtils.getText('text_stockQuantity'), TextUtils.getText('text_allowBackorder'), TextUtils.getText('text_sku'), TextUtils.getText('text_price'), TextUtils.getText('text_salePrice'), TextUtils.getText('text_weight'), TextUtils.getText('text_length'), TextUtils.getText('text_height'), TextUtils.getText('text_width'), ''];
  @Input() options: IOption[];
  @Input() websiteId: number;
  @Input() productForm: FormGroup;
  @Input() variants: IVariation[];
  searchTerm = new FormControl('');
  hiddenSearchTerm = new FormControl('');
  private _destroyRef = inject(DestroyRef);
  variantsForm: FormGroup;
  originalVariantsForm: FormGroup;
  defaultFormValue: any = {};
  allVariantsCheckboxChecked: boolean = false;
  variantsCheckboxPartiallyChecked: boolean = false;
  allHiddenVariantsCheckboxChecked: boolean = false;
  hiddenVariantsCheckboxPartiallyChecked: boolean = false;
  ProductVariantStatus = ProductVariantStatus;
  track_stock = false;
  checkedControls: AbstractControl[] = [];
  hiddenCheckedControls: AbstractControl[] = [];
  measurements = this._productAddHelper.measurements.value;
  currencySymbol = this._productAddHelper.currency.value;
  statusOptions = [
    {
      value: ProductStockStatus.IN_STOCK,
      label: TextUtils.getText('text_inStock')
    },
    {
      value: ProductStockStatus.OUT_OF_STOCK,
      label: TextUtils.getText('text_outOfStock')
    },
    {
      value: ProductStockStatus.ON_BACKORDER,
      label: TextUtils.getText('text_onBackorder')
    },
  ];
  backorderStatusOptions = [
    {
      value: ProductBackorderStatus.DO_NOT_ALLOW,
      label: TextUtils.getText('text_dontAllow')
    },
    {
      value: ProductBackorderStatus.ALLOW_NOTIFY,
      label: TextUtils.getText('text_allowButNotifyCustomer')
    },
    {
      value: ProductBackorderStatus.ALLOW,
      label: TextUtils.getText('text_allow')
    },
  ];
  mainSearchOptions = [];
  hiddenSearchOptions = [];
  searchFocused = false;
  showEditStatus = false;
  showEditBOStatus = false;

  constructor(
    private readonly _activeModal: DbActiveModal,
    private _fb: FormBuilder,
    private _productAddHelper: ProductAddHelperService,
    private _modalService: DbModalService
  ) {
  }

  ngOnInit() {
    const optionsList = this._combineOptions(this.options);
    this.variantsForm = this.copyVariantsForm(this.originalVariantsForm);
    this._addVariants(optionsList);
    this._listenSearch();
    this.onTrackStockChange(!!this.productForm.get('inventory_group').get('track_quantity').value);
  }

  onTrackStockChange(value = !this.track_stock) {
    this.track_stock = value;
    if (this.track_stock) {
      this.heads = this.heads2;
    } else {
      this.heads = this.headsMain;
    }
  }

  hideDropdown(): void {
    setTimeout(() => {
      this.searchFocused = false;
    }, 200);
  }

  selectOption(option: string) {
    this.searchTerm.setValue(option);
    this.searchFocused = false;
  }

  private _listenSearch() {
    this.searchTerm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      ).subscribe(searchTerm => {
      const uniqueOptions = new Set<string>();

      this.mainSearchOptions = [];
      this.variantsFormArray.controls.map((control) => {
        const options = control.get('options').value as Array<{ name: string }>;
        const containsSearchTerm = options.some(option => {
          const contains = option.name.toLowerCase().includes(searchTerm.toLowerCase());
          if (contains) {
            uniqueOptions.add(option.name);
          }
          return contains;
        });
        control.get('show').setValue(containsSearchTerm);
      });

      this.mainSearchOptions = Array.from(uniqueOptions);
    });

    this.hiddenSearchTerm.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(searchTerm => {
      const uniqueOptions = new Set<string>();
      this.hiddenSearchOptions = [];
      this.hiddenVariations.map((control) => {
        const options = control.get('options').value as Array<{ name: string }>;
        const containsSearchTerm = options.some(option => {
          const contains = option.name.toLowerCase().includes(searchTerm.toLowerCase());
          if (contains) {
            uniqueOptions.add(option.name); // Add to Set to ensure uniqueness
          }
          return contains;
        });
        control.get('show').setValue(containsSearchTerm);
      });
      this.hiddenSearchOptions = Array.from(uniqueOptions);
    });
  }

  get variantsFormArray(): FormArray {
    return this.variantsForm.get('variants') as FormArray;
  }

  get hiddenVariations(): AbstractControl[] {
    return this.variantsFormArray.controls.filter((control) => control.get('status').value === ProductVariantStatus.PRIVATE);
  }

  onMainCheckboxChange(value: boolean) {
    this.allVariantsCheckboxChecked = value;
    this.variantsFormArray.controls.map(control => {
      (control as FormGroup).get('checked').setValue(value);
    });
    this.variantsCheckboxPartiallyChecked = !this.allVariantsCheckboxChecked && this.variantsFormArray.controls
      .some(control => !(control as FormGroup).get('checked').value);
    this.checkedControls = this.variantsFormArray.controls
      .filter(control => (control as FormGroup).get('checked').value === true);
  }

  onCheckboxClick() {
    this.allVariantsCheckboxChecked = !this.variantsFormArray.controls
      .some(control => !(control as FormGroup).get('checked').value);
    this.variantsCheckboxPartiallyChecked = !this.allVariantsCheckboxChecked && this.variantsFormArray.controls
      .some(control => !(control as FormGroup).get('checked').value);
    this.checkedControls = this.variantsFormArray.controls
      .filter(control => (control as FormGroup).get('checked').value === true);
  }

  onMainHiddenCheckboxChange(value: boolean) {
    this.allHiddenVariantsCheckboxChecked = value;
    this.hiddenVariations.map(control => {
      (control as FormGroup).get('checked_hidden').setValue(value);
    });
    this.hiddenVariantsCheckboxPartiallyChecked = !this.allHiddenVariantsCheckboxChecked && this.hiddenVariations
      .some(control => !(control as FormGroup).get('checked_hidden').value);
    this.hiddenCheckedControls = this.hiddenVariations
      .filter(control => (control as FormGroup).get('checked_hidden').value === true);
  }

  onHiddenCheckboxClick() {
    this.allHiddenVariantsCheckboxChecked = !this.hiddenVariations
      .some(control => !(control as FormGroup).get('checked_hidden').value);
    this.hiddenVariantsCheckboxPartiallyChecked = !this.allHiddenVariantsCheckboxChecked && this.hiddenVariations
      .some(control => !(control as FormGroup).get('checked_hidden').value);
    this.hiddenCheckedControls = this.hiddenVariations
      .filter(control => (control as FormGroup).get('checked_hidden').value === true);
  }

  private _addVariants(optionsList: Array<{ name: string }[]>) {
    optionsList.map((optionArray, index) => {
      const variantData: any = {};
      const variant = this.variants && this.variants[index];
      if (variant) {
        if (variant.manage_stock) {
          this.onTrackStockChange(true);
        }
        variantData.id = variant.id;
        variantData.index = index;
        variantData.image = variant.image || null;
        variantData.status = variant.status;
        variantData.price = variant.regular_price || variant.price;
        variantData.sale_price = variant.sale_price;
        variantData.stock_status = variant.stock_status;
        variantData.stock_quantity = variant.stock_quantity;
        variantData.backorder_status = variant.backorder_status || variant.backorders;
        variantData.available = variant.stock_quantity;
        variantData.height = variant.dimensions?.height;
        variantData.weight = variant.weight;
        variantData.width = variant.dimensions?.width;
        variantData.length = variant.dimensions?.length;
        variantData.sku = variant.sku;
      }

      const newForm = this._createVariantsForm({}, index);
      const data = {
        options: optionArray,
      };
      newForm.patchValue({...data, ...this._getMainPriceFormValue(), ...variantData});
      const variantForm = this.variantsFormArray.at(index);
      if (variantForm) {
        variantForm.patchValue(newForm);
        // here to prevent status to change VALID
        variantForm.get('status').setValue(newForm.get('status').value);
      } else {
        this.variantsFormArray.push(newForm);
      }
    });
    this.defaultFormValue = this.variantsForm.getRawValue();
  }

  private _getMainPriceFormValue() {
    const formGroup = this.productForm.get('pricing_group') as FormGroup;
    return formGroup.getRawValue();
  }

  saveVariants() {
    if (this.variantsForm.valid) {
      this.variantsForm.get('saved').setValue(true);
      this.productForm.get('inventory_group').get('track_quantity').setValue(this.track_stock);
      this.productForm.get('inventory_group').get('track_quantity').markAsDirty();
      const value = this.variantsForm.getRawValue();
      this.originalVariantsForm.patchValue(value);
      this._activeModal.close();
      this._resetCheckboxes();
    }
  }

  private _createVariantsForm(withValue = {}, index: number) {
    const data = {
      index: index,
      id: null,
      checked: false,
      image: null,
      checked_hidden: false,
      show: true,
      options: [],
      stock_quantity: 0,
      stock_status: ProductStockStatus.IN_STOCK,
      backorder_status: ProductBackorderStatus.DO_NOT_ALLOW,
      price: new FormControl('', this._productAddHelper.priceValidation),
      sale_price: new FormControl('', this._productAddHelper.priceValidation),
      available: new FormControl('', this._productAddHelper.priceValidation),
      sku: '',
      weight: '',
      height: '',
      length: '',
      width: '',
      status: ProductVariantStatus.PUBLISH
    };
    if (withValue['options']) {
      data.options = withValue['options'];
      const group = this._fb.group(data);
      group.patchValue(withValue);

      return group;
    }
    return this._fb.group(data);
  }

  private _combineOptions(arrays: any[]): Array<{ name: string }[]> {
    const optionValues = arrays
      .map(obj => obj.option_value)
      .filter(options => Array.isArray(options) && options.length > 0);

    return optionValues.reduce((acc, values) => {
      if (acc.length === 0) {
        return values.map(value => {
          return [value];
        });
      } else {
        return acc.flatMap(combination =>
          values.map(value => [...combination, value])
        );
      }
    }, []);
  }

  resetForm() {
    this.variantsFormArray.controls.map((control, i) => {
      const checked = control.get('checked');
      if (checked.value) {
        checked.setValue(false);
        control.patchValue(this.defaultFormValue.variants[i]);
      }
    });
  }

  private _resetCheckboxes() {
    this.variantsFormArray.controls.forEach((control) => {
      control.get('checked').setValue(false);
    });
  }

  private _clearVariantsArray() {
    while (this.variantsFormArray.length !== 0) {
      this.variantsFormArray.removeAt(0);
    }
  }

  openMedia(form: AbstractControl) {
    const modal = this._modalService.open(MediaLibraryComponent);
    modal.componentInstance.websiteId = this.websiteId;
    modal.componentInstance.forMain = true;
    modal.closed.subscribe((images: IMedia[]) => {
      if (!images.at(0)) {
        return;
      }
      form.get('image').setValue(images.at(0));
      form.get('image').markAllAsTouched();
    });
  }

  copyVariantsForm(form: FormGroup): FormGroup {
    const variants = form.get('variants') as FormArray;
    const newForm = this._fb.group({
      variants: this._fb.array([]),
      saved: false
    });
    const newFormVariants = newForm.get('variants') as FormArray;
    variants.controls.map((variant, index) => {
      newFormVariants.push(this._createVariantsForm(variant.getRawValue(), index));
    });

    return newForm;
  }


  batchEditStatus(value) {
    const {status} = value;
    const controls = this.checkedControls;
    controls.map(control => {
      if (control.get('status').value !== this.ProductVariantStatus.PRIVATE) {
        control.get('stock_status').setValue(status);
      }
    });
  }

  batchEditBackorderStatus(value) {
    const {status} = value;
    const controls = this.checkedControls;
    controls.map(control => {
      if (control.get('status').value !== this.ProductVariantStatus.PRIVATE) {
        control.get('backorder_status').setValue(status);
      }
    });
  }

  batchEditDimensions(value) {
    const {width, height, length, weight} = value;
    const controls = this.checkedControls;

    controls.map(control => {
      if (control.get('status').value !== this.ProductVariantStatus.PRIVATE) {
        if (TypeUtils.isNumber(+height) && width !== '') {
          control.get('width').setValue(width);
        }
        if (TypeUtils.isNumber(+height) && height !== '') {
          control.get('height').setValue(height);
        }
        if (TypeUtils.isNumber(+length) && length !== '') {
          control.get('length').setValue(length);
        }
        if (TypeUtils.isNumber(+weight) && weight !== '') {
          control.get('weight').setValue(weight);
        }
      }
    });
  }

  batchHide(value = ProductVariantStatus.PRIVATE, group?) {
    const controls = group || this.checkedControls;
    controls.map(control => {
      if (value) {
        control.get('status').setValue(value);
      }
    });
  }

  batchEditPrice(value) {
    const {price, sale_price} = value;
    const controls = this.checkedControls;
    controls.map(control => {
      if (control.get('status').value !== this.ProductVariantStatus.PRIVATE) {
        if (TypeUtils.isNumber(+price) && price !== '') {
          control.get('price').setValue(price);
        }

        if (sale_price && TypeUtils.isNumber(+sale_price) && sale_price !== '') {
          control.get('sale_price').setValue(sale_price);
        }
      }
    });
  }

  batchEditInventory(value) {
    const {inventory} = value;
    const controls = this.checkedControls;
    controls.map(control => {
      if (control.get('status').value !== this.ProductVariantStatus.PRIVATE) {
        control.get('stock_quantity').setValue(inventory);
      }
    });
  }

  ngOnDestroy() {
    // if (!this.variantsForm.get('saved').value) {
    //   this._clearVariantsArray();
    // }
  }

}
